import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from '@kognitos/lattice';
import { z } from 'zod';

import type { Bdkv2CredentialDescriptor } from '@/generated/hooks/bdk/models';
import {
  ECredentialScope,
  isCredentialNameConflict,
  type ICredentialCreateFormValues,
  type TCredentialSelection,
} from '@/modules/credentials';
import { captureException } from '@/modules/sentry';

import { createAuthenticationCredentialsSchema } from '../schemas';

/** A resolved credential, plus the consent URL when the user must sign in first. */
export interface IResolvedCredential {
  credentialId: string;
  authorizeUrl?: string;
}

/** Arguments for {@link useManagedCredential}. */
export interface IUseManagedCredentialArgs {
  /** Fields of the create form (credential fields, or OAuth arguments). */
  fieldDescriptors: Bdkv2CredentialDescriptor[];
  /** Name a new credential gets unless the user renames it. */
  defaultDisplayName: string;
  /**
   * Start in create mode: there is nothing to pick, so skip the list. Applies
   * until the user makes a choice.
   */
  defaultToCreate: boolean;
  /** Use this credential and offer no choice (a shared secret). */
  fixedCredentialId?: string;
  /** Selection to start from (e.g. a credential just authorized). */
  initialSelection?: TCredentialSelection | null;
  /** Creates the credential from the form's values. */
  create: (values: ICredentialCreateFormValues) => Promise<IResolvedCredential>;
}

const createCredentialFormSchema = (
  fieldDescriptors: Bdkv2CredentialDescriptor[],
) =>
  z.object({
    // Blank falls back to the suggested name.
    displayName: z.string(),
    scope: z.enum([ECredentialScope.Workspace, ECredentialScope.Organization]),
    values: createAuthenticationCredentialsSchema(fieldDescriptors),
  });

/**
 * The credential choice for one environment of the connect wizard: which
 * existing credential to use, or the form for a new one. `resolveCredential`
 * turns the choice into a credential id, creating the credential first when
 * needed; once created, the selection points at it, so retrying never creates
 * a duplicate.
 *
 * @param args - Hook arguments.
 * @returns The selection, the create form, and `resolveCredential`.
 */
export function useManagedCredential({
  fieldDescriptors,
  defaultDisplayName,
  defaultToCreate,
  fixedCredentialId,
  initialSelection = null,
  create,
}: IUseManagedCredentialArgs) {
  const { t } = useTranslation('credentials');
  const [chosenSelection, setSelection] = useState<TCredentialSelection | null>(
    initialSelection,
  );
  const selection: TCredentialSelection | null = fixedCredentialId
    ? { mode: 'existing', credentialId: fixedCredentialId }
    : (chosenSelection ?? (defaultToCreate ? { mode: 'create' } : null));

  const formSchema = useMemo(
    () => createCredentialFormSchema(fieldDescriptors),
    [fieldDescriptors],
  );
  const form = useForm<ICredentialCreateFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      displayName: '',
      scope: ECredentialScope.Workspace,
      values: {},
    },
  });

  const reset = () => {
    setSelection(null);
    form.reset();
  };

  /**
   * Resolves the selection to a credential, creating it when "create new" is
   * selected. Returns `null` when nothing is selected, the form is invalid, or
   * the create failed (errors are shown on the form or as a toast).
   */
  const resolveCredential = async (): Promise<IResolvedCredential | null> => {
    if (!selection) return null;
    if (selection.mode === 'existing') {
      return { credentialId: selection.credentialId };
    }
    if (!(await form.trigger())) return null;

    const values = form.getValues();
    try {
      const resolved = await create({
        ...values,
        displayName: values.displayName.trim() || defaultDisplayName,
      });
      setSelection({ mode: 'existing', credentialId: resolved.credentialId });
      form.reset();
      return resolved;
    } catch (error) {
      if (isCredentialNameConflict(error)) {
        form.setError('displayName', {
          message: t('credentials.validation.displayNameTaken'),
        });
      } else {
        toast.error(t('credentials.errors.createFailed'));
        captureException(error as Error, {
          tags: { module: 'integrations', action: 'createManagedCredential' },
        });
      }
      return null;
    }
  };

  return { selection, setSelection, form, resolveCredential, reset };
}
